import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';

/**
 * Invisible Tab stop placed immediately after the skip link in every portal shell.
 *
 * The skip link (neo-skip-to-content) lives inside cm-root, which is placed before
 * ce-neogov-top-navbar in index.html for correct DOM / focus order. That means every
 * element inside cm-root — including the whole app — precedes the navbar in tab order.
 * This bridge element is the single focusable thing between the skip link and the rest
 * of the app; when Tab lands here it immediately moves focus into the navbar shadow DOM,
 * making the effective tab sequence: Skip link → Navbar → Content (WCAG 2.4.3).
 *
 * Shift+Tab from the navbar's first element is handled by detecting the tab direction
 * via a capturing keydown listener so that backward navigation returns to the skip link
 * instead of looping back into the navbar.
 */
@Component({
  selector: 'cm-nav-focus-bridge',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './nav-focus-bridge.component.html',
  styleUrl: './nav-focus-bridge.component.less',
})
export class NavFocusBridgeComponent implements OnInit, OnDestroy {
  private _lastKeyWasShiftTab = false;

  private readonly _onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Tab') this._lastKeyWasShiftTab = e.shiftKey;
  };

  public ngOnInit(): void {
    document.addEventListener('keydown', this._onKeydown, true);
  }

  public ngOnDestroy(): void {
    document.removeEventListener('keydown', this._onKeydown, true);
  }

  protected onFocus(): void {
    if (this._lastKeyWasShiftTab) return;

    requestAnimationFrame(() => this._focusNavbar());
  }

  protected onActivate(): void {
    this._focusNavbar();
  }

  private _focusNavbar(): void {
    const navbar = document.querySelector<HTMLElement>('ce-neogov-top-navbar');
    if (!navbar) return;

    if (navbar.shadowRoot) {
      const el = this._firstFocusableIn(navbar.shadowRoot);
      if (el) {
        el.focus();
        return;
      }
    }

    if (!navbar.hasAttribute('tabindex')) navbar.setAttribute('tabindex', '-1');
    navbar.focus();
  }

  private _firstFocusableIn(root: ShadowRoot | Element): HTMLElement | null {
    const SELECTOR =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const direct = root.querySelector<HTMLElement>(SELECTOR);
    if (direct) return direct;

    for (const child of root.querySelectorAll('*')) {
      if ((child as Element & { shadowRoot?: ShadowRoot }).shadowRoot) {
        const found = this._firstFocusableIn(
          (child as Element & { shadowRoot: ShadowRoot }).shadowRoot,
        );
        if (found) return found;
      }
    }

    return null;
  }
}
